import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { StarsButton, Translation } from '@ps/web-shared-components';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import { DocumentType } from '@swf/core/enums';
import { select } from '@swf/core/store';

import {
  TXTCLI_If_your_document_doesnXt_inclu_ELL,
  TXTCLI_Upload_My_DriverXs_License,
  TXTCLI_Upload_My_National_ID_Card,
  TXTCLI_Upload_My_Passport,
  TXTCLI_Uploading_your_DriverXs_Licens_ELL,
  TXTCLI_Uploading_your_National_ID_Car_ELL,
  TXTCLI_Uploading_your_Passport_for_ve_ELL,
  TXTCLI_WeXll_need_a_picture_of_the_pa_ELL,
  TXTWCL_Take_a_picture_of_my_document,
  TXTWCL_Upload_my_document
} from './DocumentInformationBox.messages.json';
import styles from './DocumentInformationBox.scss';

const updatingYourDocumentText = {
  [DocumentType.Passport]: TXTCLI_Uploading_your_Passport_for_ve_ELL,
  [DocumentType.DrivingLicense]: TXTCLI_Uploading_your_DriverXs_Licens_ELL,
  [DocumentType.NationalIDCard]: TXTCLI_Uploading_your_National_ID_Car_ELL
};

const uploadMyDocumentButtonText = {
  [DocumentType.Passport]: TXTCLI_Upload_My_Passport,
  [DocumentType.DrivingLicense]: TXTCLI_Upload_My_DriverXs_License,
  [DocumentType.NationalIDCard]: TXTCLI_Upload_My_National_ID_Card
};

interface Props {
  documentType: DocumentType;
  onUploadClick(): void;
  showFileSystem(): void;
  showSmartCapture(): void;
}

const DocumentInformationBox: React.FC<Props> = ({
  documentType,
  onUploadClick,
  showFileSystem,
  showSmartCapture
}) => {
  const IDScanSDK = useFeature(FeatureFlag.IDScanSDK);
  const IDScanHosted = useFeature(FeatureFlag.IDScanHosted);
  const isMobile = useSelector(select.device.isMobile);
  const amlDepositThresholdReachedTimestamp = useSelector(
    select.account.getAmlDepositThresholdReachedTimestamp
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobile && scrollRef.current !== null) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  });

  return (
    <div className={styles.documentInformationContainer} ref={scrollRef}>
      <span className={styles.uploadingYourDocumentText}>
        <Translation {...updatingYourDocumentText[documentType]} />
      </span>
      <Translation tagName="p" {...TXTCLI_WeXll_need_a_picture_of_the_pa_ELL} />
      <Translation tagName="p" {...TXTCLI_If_your_document_doesnXt_inclu_ELL} values={{ 0: 3 }} />
      {IDScanSDK || IDScanHosted ? (
        <ButtonGroup>
          <StarsButton
            onClick={() => {
              onUploadClick();
              showSmartCapture();
            }}
            className={styles.idscanUploadButton}
            translation={TXTWCL_Take_a_picture_of_my_document}
          />
          <StarsButton
            variant="secondary"
            onClick={() => {
              onUploadClick();
              showFileSystem();
            }}
            translation={TXTWCL_Upload_my_document}
          />
        </ButtonGroup>
      ) : (
        <StarsButton
          className={styles.uploadButton}
          onClick={onUploadClick}
          translation={uploadMyDocumentButtonText[documentType]}
          trackingValue={amlDepositThresholdReachedTimestamp}
        />
      )}
    </div>
  );
};

export default DocumentInformationBox;
